import { FormEvent, useState } from 'react';
import type { User } from '@rosita/shared';
import { api, extractError } from '../../lib/api';
import { PhoneInput } from '../ui/PhoneInput';
import { useToast } from './Toast';

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-1.5 text-sm hover:bg-admin-surface-alt';

interface EditForm {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onSaved: () => void;
}

export function EditUserModal({ user, onClose, onSaved }: EditUserModalProps): JSX.Element {
  const toast = useToast();
  const [form, setForm] = useState<EditForm>({
    email: user.email,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    phone: user.phone ?? '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}`, {
        email: form.email.trim().toLowerCase(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
      });
      toast.success('Utilizador atualizado.');
      onSaved();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao guardar alterações'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-lg font-semibold">Editar utilizador</h2>
        <p className="mb-4 text-xs text-admin-muted">{user.email}</p>
        <form onSubmit={submit} className="grid gap-3">
          <div>
            <label className="block text-sm text-admin-text-soft">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className={`${inputClass} mt-1 w-full`}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm text-admin-text-soft">Primeiro nome</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                required
                className={`${inputClass} mt-1 w-full`}
              />
            </div>
            <div>
              <label className="block text-sm text-admin-text-soft">Apelido</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                required
                className={`${inputClass} mt-1 w-full`}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-admin-text-soft">Telefone</label>
            <PhoneInput
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className={secondaryBtn}
            >
              Cancelar
            </button>
            <button type="submit" disabled={saving} className={primaryBtn}>
              {saving ? 'A guardar…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
